// Full-document render of the store-locator detail page: PageSkeleton
// wrapping the Google Map hero (StoreDetailMap), the store info/hours/
// details, the print / directions / send-details actions, and the optional
// current-offer image. The meta_robots noindex (Houston / Harrisburg) and
// canonical (Sacramento / Lubbock) overrides are threaded through page_chrome
// server-side, so PageSkeleton emits them.
//
// The LocatorSendStoreDetails include_react island is inlined as the
// <LocatorSendSingleStore> component (one React tree; redux Provider is global).
import React from "react";
import { isoSyncStoreID } from "tsi-common-react/src/models/nominals";
import { useStaticURL } from "tsi-common-react/src/utils/urls";
import { t } from "ttag";

import { templates } from "@reactivated";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { LocatorSendSingleStore } from "../apps/retail-locator/LocatorSendSingleStore";
import { PageSkeleton } from "../components/PageSkeleton";
import { StoreDetailMap } from "../components/StoreDetailMap";

// Mutable reactivated props (not Immutable<>): this template composes
// PageSkeleton, which is typed with mutable reactivated props.
type IProps = templates.StoreDetailTemplate;

export const Template = (props: IProps): React.JSX.Element => {
    const staticURL = useStaticURL();
    const { store, hours } = props;
    return (
        <PageSkeleton pageChrome={props.page_chrome}>
            <StoreDetailMap
                lat={store.lat}
                lng={store.lng}
                staticMapUrl={props.static_map_url}
            />

            <section className="info">
                <div className="info__item info__address">
                    <h1 className="info__headline">{store.name}</h1>
                    <p className="info__contact">
                        <span className="info__address1">{store.address}</span>
                        <span className="info__address2">
                            {store.address2 ? store.address2 : null}
                        </span>
                        <span className="info__city">{store.city}</span>,{" "}
                        <span className="info__state">{store.state}</span>{" "}
                        <span className="info__postal">{store.postal}</span>
                        <br />
                        <span className="info__phone">
                            {store.phone_national}
                        </span>
                        {store.web && (
                            <>
                                <br />
                                <a
                                    href={store.web}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="info__website"
                                >
                                    {t`Website`}
                                    <img
                                        src={`${staticURL}img/locator/open-link-icon.png`}
                                        alt={t`(opens in a new window)`}
                                        loading="lazy"
                                        decoding="async"
                                    />
                                </a>
                            </>
                        )}
                    </p>
                    <img
                        src={props.storefront_image_url}
                        className="info__storefront-image responsive-img"
                        alt={props.storefront_image_alt}
                        loading="lazy"
                        decoding="async"
                    />
                </div>

                {props.has_details && hours && (
                    <>
                        <div className="info__item info__hours">
                            <h2 className="info__headline">{t`Store Hours`}</h2>
                            <ul className="info__hours-list info__content">
                                <li className="info__hours-list-item">
                                    {t`Sunday`} <span>{hours.sun}</span>
                                </li>
                                <li className="info__hours-list-item">
                                    {t`Monday`} <span>{hours.mon}</span>
                                </li>
                                <li className="info__hours-list-item">
                                    {t`Tuesday`} <span>{hours.tues}</span>
                                </li>
                                <li className="info__hours-list-item">
                                    {t`Wednesday`} <span>{hours.wed}</span>
                                </li>
                                <li className="info__hours-list-item">
                                    {t`Thursday`} <span>{hours.thurs}</span>
                                </li>
                                <li className="info__hours-list-item">
                                    {t`Friday`} <span>{hours.fri}</span>
                                </li>
                                <li className="info__hours-list-item">
                                    {t`Saturday`} <span>{hours.sat}</span>
                                </li>
                            </ul>
                        </div>
                        <div className="info__item info__details">
                            <h2 className="info__headline">{t`Store Details`}</h2>
                            <p className="info__content">
                                {props.details_text}
                            </p>
                        </div>
                    </>
                )}

                <div className="info__actions">
                    <div className="info__actions-item info__actions-print">
                        <button
                            type="button"
                            title={t`Print`}
                            className="info__actions-icon"
                            onClick={() => {
                                getWindow()?.print();
                            }}
                        >
                            <img
                                src={`${staticURL}img/locator/icon-print.svg`}
                                alt={t`Print icon`}
                                loading="lazy"
                                decoding="async"
                            />
                        </button>
                        <span className="info__actions-label">{t`Print`}</span>
                    </div>

                    <div className="info__actions-item info__actions-directions">
                        <a
                            href={props.directions_url}
                            title={t`Directions`}
                            className="info__actions-icon"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <img
                                src={`${staticURL}img/locator/icon-directions.svg`}
                                alt={t`Directions icon`}
                                loading="lazy"
                                decoding="async"
                            />
                        </a>
                        <span className="info__actions-label">
                            {t`Directions`}
                        </span>
                    </div>

                    <div
                        className="send__item info__actions-item modal store-locator-survey"
                        id="send-email"
                    >
                        <LocatorSendSingleStore
                            storeID={isoSyncStoreID.wrap(store.external_id)}
                        />
                    </div>
                </div>
            </section>

            {props.offer_image_url && (
                <section className="current-offer">
                    <img
                        src={props.offer_image_url}
                        alt={t`Offer`}
                        className="responsive-img"
                        loading="lazy"
                        decoding="async"
                    />
                </section>
            )}
        </PageSkeleton>
    );
};
